import React from "react";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

function Pagination({ currentPage, totalPages, onPageChange }: PaginationProps) {
  // Функция для генерации списка страниц с умной логикой троеточия
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    
    // Если страниц мало - показываем все
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
      return pages;
    }

    // Всегда показываем первую страницу
    pages.push(1);

    // Логика для левой части
    if (currentPage <= 4) {
      // Если текущая страница близко к началу
      for (let i = 2; i <= Math.min(5, totalPages - 1); i++) {
        pages.push(i);
      }
      if (totalPages > 6) {
        pages.push("...");
      }
    } else if (currentPage >= totalPages - 3) {
      // Если текущая страница близко к концу
      if (totalPages > 6) {
        pages.push("...");
      }
      for (let i = Math.max(2, totalPages - 4); i <= totalPages - 1; i++) {
        pages.push(i);
      }
    } else {
      // Если текущая страница в середине
      pages.push("...");
      for (let i = currentPage - 1; i <= currentPage + 1; i++) {
        pages.push(i);
      }
      pages.push("...");
    }

    // Всегда показываем последнюю страницу (если она не первая)
    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages;
  };

  // Функция для обработки клика на троеточие
  const handleEllipsisClick = (index: number) => {
    const pages = getPageNumbers();
    const ellipsisIndex = pages.findIndex((page, i) => page === "..." && i === index);
    
    if (ellipsisIndex === -1) return;
    
    // Определяем, какая это троеточие (левое или правое)
    const leftEllipsis = ellipsisIndex < pages.length / 2;
    
    if (leftEllipsis) {
      // Левое троеточие - переходим к странице в середине левой части
      const targetPage = Math.max(2, Math.floor(currentPage / 2));
      onPageChange(targetPage);
    } else {
      // Правое троеточие - переходим к странице в середине правой части
      const targetPage = Math.min(totalPages - 1, Math.floor((currentPage + totalPages) / 2));
      onPageChange(targetPage);
    }
  };

  // Функция для получения информации о троеточии для тултипа
  const getEllipsisTooltip = (index: number) => {
    const pages = getPageNumbers();
    const ellipsisIndex = pages.findIndex((page, i) => page === "..." && i === index);
    
    if (ellipsisIndex === -1) return "";
    
    const leftEllipsis = ellipsisIndex < pages.length / 2;
    
    if (leftEllipsis) {
      const startPage = 2;
      const endPage = Math.min(5, totalPages - 1);
      return `Показать страницы ${startPage}-${endPage}`;
    } else {
      const startPage = Math.max(2, totalPages - 4);
      const endPage = totalPages - 1;
      return `Показать страницы ${startPage}-${endPage}`;
    }
  };

  const pages = getPageNumbers();

  return (
    <div className="flex flex-wrap items-center justify-center gap-1 sm:gap-2 mt-8 px-2">
      {/* Кнопка Назад */}
      <button
        className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center border rounded-full bg-white shadow hover:bg-gray-100 disabled:opacity-50 text-sm sm:text-base"
        disabled={currentPage === 1}
        onClick={() => onPageChange(currentPage - 1)}
        title="Предыдущая страница"
      >
        ←
      </button>

      {/* Страницы */}
      {pages.map((page, index) =>
        typeof page === "number" ? (
          <button
            key={index}
            className={`px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm border rounded ${
              currentPage === page ? "font-bold text-black border-black" : "text-gray-500"
            } hover:bg-gray-100 transition-colors`}
            onClick={() => onPageChange(page)}
          >
            {page}
          </button>
        ) : (
          <button
            key={index}
            className="px-1 py-1 sm:px-2 sm:py-2 text-xs sm:text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded cursor-pointer transition-colors"
            onClick={() => handleEllipsisClick(index)}
            title={getEllipsisTooltip(index)}
          >
            ...
          </button>
        )
      )}

      {/* Кнопка Вперед */}
      <button
        className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center border rounded-full bg-white shadow hover:bg-gray-100 disabled:opacity-50 text-sm sm:text-base"
        disabled={currentPage === totalPages}
        onClick={() => onPageChange(currentPage + 1)}
        title="Следующая страница"
      >
        →
      </button>
    </div>
  );
}

export default Pagination;